/**
 * identity-service `/login` + `/login/totp` client (uid-001-login-frontdoor-ui).
 *
 * Hits `POST {VITE_IDENTITY_SERVICE_URL}/login` with the `LoginRequest` shape
 * (verified against MERGED identity-service `src/session.rs` +
 * `src/session_handlers.rs` + `src/totp_handlers.rs`). The password is sent ONLY
 * over HTTPS in the request body and is NEVER logged, stored, or echoed.
 *
 * Unlike `/register`, `/login` DOES issue a session. On a successful (non-2FA)
 * login the server returns `200 + {jwt, refresh_token, mfa_required:false}` and,
 * when a `return_origin` was supplied, ALSO `{code, redirect:"<origin>?code=<code>"}`
 * — the one-time, ~60s, single-use cross-domain handoff code the consuming suite
 * exchanges at `POST /token/exchange`. Our job ends at handing back that `redirect`.
 *
 * 2FA: a TOTP-enrolled account gets `200 + {mfa_required:true, mfa_token}` (NO jwt)
 * — the caller must complete `POST /login/totp {mfa_token, code}` to get the final
 * `{jwt, ...}`. The `/login/totp` response does NOT itself carry the cross-domain
 * `code`/`redirect` (the server only mints those on the `/login` success path), so
 * the 2FA completion is keyed off the SAME `return_origin`: we re-derive the
 * redirect from the originally-validated origin (see LoginPage).
 *
 * The refresh token is ALSO set as an httpOnly `Set-Cookie` by identity-service
 * (PR #36 — refresh-cookie + credentialed CORS); we send the request with
 * `credentials: 'include'` so the browser stores it. We never read the cookie or
 * the body `refresh_token` from JS — the suite's `/token/exchange` landing handles
 * the session. (Dependency: the credentialed-CORS preflight on identity-service is
 * provided by PR #36; until it deploys the cross-origin POST will be blocked.)
 */

export interface LoginRequest {
  email: string;
  password: string;
  /**
   * The allowlisted absolute origin to hand the session back to via the one-time
   * code. Omitted from the body when absent (a direct/central-origin login). MUST
   * already be validated against the cross-origin allowlist by the caller — the
   * server independently re-validates it against its OWN allowlist (a non-allowed
   * origin is a 400 regardless of credentials).
   */
  return_origin?: string;
}

export interface LoginTotpRequest {
  /** The single-use `mfa_token` from the `/login` 2FA challenge. */
  mfa_token: string;
  /** The current 6-digit TOTP code from the authenticator app. */
  code: string;
}

/**
 * Discriminated outcome of a login attempt, classified by HTTP status + body into
 * the distinct UI branches the story requires.
 *
 *  - `success` carries the `redirect` (`<return_origin>?code=<code>`) when the
 *    login was cross-domain; for a direct login it is absent.
 *  - `mfa` carries the single-use `mfa_token` the UI presents at `/login/totp`.
 *  - `unauthorized` is the constant-time, non-enumerating 401 (bad creds).
 *  - `locked` carries the `Retry-After` seconds parsed from the 429 header.
 *  - `error` is every other failure (5xx / network / sealed-503 / unexpected).
 */
export type LoginResult =
  | { kind: 'success'; redirect?: string }
  | { kind: 'mfa'; mfaToken: string }
  | { kind: 'unauthorized' } // 401 — bad email/password (non-enumerating)
  | { kind: 'locked'; retryAfter?: number } // 429 — lockout (fosrb-auth-3)
  | { kind: 'error' }; // 500 / 503 / network / unexpected

interface LoginSuccessBody {
  jwt?: string;
  code?: string;
  redirect?: string;
  refresh_token?: string;
  mfa_required?: boolean;
  mfa_token?: string;
}

function identityServiceUrl(): string {
  const base = import.meta.env.VITE_IDENTITY_SERVICE_URL;
  if (!base) {
    // Surfaced as a generic error in the UI; the deploy build-arg wires this.
    throw new Error('VITE_IDENTITY_SERVICE_URL is not configured');
  }
  return base.replace(/\/$/, '');
}

/** Parse a `Retry-After` header (seconds form) into a positive integer, or undefined. */
function parseRetryAfter(res: Response): number | undefined {
  const raw = res.headers?.get?.('Retry-After');
  if (!raw) {
    return undefined;
  }
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * `POST /login`. Sends credentials (+ optional allowlisted `return_origin`) and
 * classifies the response. `credentials: 'include'` so the httpOnly refresh cookie
 * identity-service sets is stored by the browser.
 */
export async function login(req: LoginRequest): Promise<LoginResult> {
  const body: LoginRequest = {
    email: req.email,
    password: req.password,
  };
  if (req.return_origin) {
    body.return_origin = req.return_origin;
  }

  let res: Response;
  try {
    res = await fetch(`${identityServiceUrl()}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
  } catch {
    // Network/CORS failure — generic, no detail (no PII, no internals).
    return { kind: 'error' };
  }

  switch (res.status) {
    case 200: {
      let data: LoginSuccessBody;
      try {
        data = (await res.json()) as LoginSuccessBody;
      } catch {
        return { kind: 'error' };
      }
      // A 2FA challenge carries mfa_required + mfa_token and NO jwt.
      if (data.mfa_required && data.mfa_token) {
        return { kind: 'mfa', mfaToken: data.mfa_token };
      }
      // Non-2FA success. `redirect` is present only for the cross-domain flow.
      return { kind: 'success', redirect: data.redirect };
    }
    case 401:
      return { kind: 'unauthorized' };
    case 429:
      return { kind: 'locked', retryAfter: parseRetryAfter(res) };
    default:
      // 400 (return_origin not allowed — a caller bug, since we pre-validate),
      // 503 (TOTP sealed), 500, and any unexpected status → generic error.
      return { kind: 'error' };
  }
}

/**
 * Discriminated outcome of the `/login/totp` 2FA completion step. Every server
 * failure (bad/expired/used mfa_token OR wrong code) collapses to the same uniform
 * 401 → `invalidCode` (non-enumerating, matching the server contract).
 */
export type LoginTotpResult =
  | { kind: 'success'; redirect?: string }
  | { kind: 'invalidCode' } // 401 — wrong/expired/used (uniform, non-enumerating)
  | { kind: 'error' }; // 503 sealed / 500 / network / unexpected

/**
 * `POST /login/totp`. Completes a 2FA login by submitting the single-use
 * `mfa_token` + the current TOTP code. On 200 the server mints the JWT + refresh
 * token (+ the httpOnly refresh cookie via `credentials: 'include'`). For a
 * cross-domain login the server ALSO returns `{code, redirect:"<origin>?code="}`
 * (bl-768 wired the mfa challenge to carry the validated `return_origin` through
 * to this step), so a 2FA cross-domain login gets the SAME `?code=` handoff the
 * non-2FA `/login` path does — `success` carries that `redirect` when present.
 */
export async function loginTotp(req: LoginTotpRequest): Promise<LoginTotpResult> {
  let res: Response;
  try {
    res = await fetch(`${identityServiceUrl()}/login/totp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ mfa_token: req.mfa_token, code: req.code }),
    });
  } catch {
    return { kind: 'error' };
  }

  switch (res.status) {
    case 200: {
      // Mirror the non-2FA `login()` 200 handling EXACTLY: an unparseable body
      // is an error (not a silent redirect-less success). `redirect` is present
      // only for the cross-domain flow (return_origin set); a direct/central
      // 2FA login omits it and the caller falls back to the resolved target.
      let data: LoginSuccessBody;
      try {
        data = (await res.json()) as LoginSuccessBody;
      } catch {
        return { kind: 'error' };
      }
      return { kind: 'success', redirect: data.redirect };
    }
    case 401:
      // Uniform non-enumerating failure: bad/expired/used token OR wrong code.
      return { kind: 'invalidCode' };
    default:
      // 503 (sealed), 500, and any unexpected status → generic error.
      return { kind: 'error' };
  }
}
